import * as React from "react";
import { observer } from "mobx-react-lite";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { Table } from "../../newcomponents/table/table";
import { TableNewItemButton } from "../../newcomponents/table/components/tableNewButton";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { Secret } from "../../schema/types/secret";
import { linksOf } from "../../services/cpln";
import { SecretTypeColumn } from "../../newcomponents/table/columns/secret/typeColumn";
import { TableActionsDropdown } from "../../newcomponents/table/components/tableActionsDropdown";
import { DeleteModal } from "../../components/modals/deleteModal";
import { NGLabelText } from "../../newcomponents/text/labelText";
import { NGCheckbox } from "../../newcomponents/checkbox";
import { Query } from "../../schema/types/query";

const STORAGE_KEY_SECRET_LIST_HIDE_HELM_STATE = "STORAGE_KEY_SECRET_LIST_HIDE_HELM_STATE";

interface SecretTableItem extends Secret {
  selfLink: string;
}

const SecretListRaw: React.FC = () => {
  const [selectedLinks, setSelectedLinks] = React.useState<string[]>([]);
  const [action, setAction] = React.useState<string>("");

  function getDefaultHideHelmState() {
    const localValue = localStorage.getItem(STORAGE_KEY_SECRET_LIST_HIDE_HELM_STATE);
    if (!localValue) {
      return true;
    }
    return localValue === "true" ? true : false;
  }
  const [hideHelmState, setHideHelmState] = React.useState(getDefaultHideHelmState());
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY_SECRET_LIST_HIDE_HELM_STATE, String(hideHelmState));
  }, [hideHelmState]);

  const helmStateQuery: Query = React.useMemo(
    () => ({
      kind: "secret",
      spec: {
        match: "none",
        terms: [
          {
            property: "name",
            op: "~",
            value: "cpln-helm-release",
          },
          {
            property: "name",
            op: "~",
            value: "cpln-release",
          },
          {
            tag: "cpln/helm",
            op: "=",
            value: "true",
          },
        ],
      },
    }),
    [],
  );
  const query = hideHelmState ? helmStateQuery : undefined;

  const { kind, id } = useTableKindId("secret", "secret-list");
  const qData = useTableItemQueryData<Secret>(kind, { query: query, fetchAllPages: true });

  const secretTableItems: SecretTableItem[] = qData.visibleItems.map((item) => {
    const selfLink = linksOf(item).self!;
    return { ...item, selfLink: selfLink };
  });

  return (
    <>
      <Table<SecretTableItem>
        title={"Secrets"}
        data={secretTableItems}
        selectKey="selfLink"
        selectMode="multi"
        selections={selectedLinks}
        onSelectionsChange={setSelectedLinks}
        tableId={id}
        headerRenderer={() => {
          return (
            <>
              <NGCheckbox isDisabled={!!qData.query} checked={hideHelmState} onChange={setHideHelmState}>
                <NGLabelText>Hide Helm State Secrets</NGLabelText>
              </NGCheckbox>
              <TableNewItemButton kind={kind} />
              <TableActionsDropdown
                kind={kind}
                selectionLinks={selectedLinks}
                options={[
                  {
                    key: "delete",
                    label: "Delete",
                    disabled: selectedLinks.length < 1,
                    danger: true,
                    callback: () => setAction("delete"),
                  },
                ]}
              />
              <TableHeaderRefreshButton onClick={qData.fetchItems} />
              <CustomTableCPLNQuery
                tableId={id}
                kind={kind}
                onQuery={qData.setQuery}
                isLoading={qData.isLoadingQueried}
              />
            </>
          );
        }}
        enableFilter
        isLoading={qData.isLoading}
        columns={[
          NameDescriptionColumn(), //
          SecretTypeColumn(),
          TagsColumn(),
          CreatedColumn(),
          LastModifiedColumn(),
        ]}
      />
      {action === "delete" ? (
        <DeleteModal
          kind={kind}
          onCancel={() => setAction("")}
          onComplete={() => {
            setAction("");
            setSelectedLinks([]);
            qData.fetchItems();
          }}
          links={selectedLinks}
        />
      ) : null}
    </>
  );
};

export const SecretList = observer(SecretListRaw);
